/**
 * Page slice for the diagram store.
 *
 * Owns every action and helper related to multi-page workspace management:
 * adding, switching, renaming, and deleting pages, as well as toggling a
 * page's diagram type and (un)linking nodes to other pages.
 *
 * The slice deliberately treats the host store as a black box: callers
 * provide an updater function (which wraps Immer's `produce`) plus a state
 * shape that satisfies {@link PageMutableState}. Initial-state helpers
 * (`makeMainPage`, `normalizePage`, `withLegacySequenceData`,
 * `syncPageFromState`) are exposed so the host store can reuse them when
 * hydrating from persistence.
 */
import { generateId } from '../../utils';
import type {
  DiagramPage,
  DiagramType,
  Edge,
  Graph,
  Layer,
  Node,
  NodeGroup,
  PageSettings,
  SequenceFragment,
  SequenceMessage,
  SequenceParticipant,
  SequenceParticipantGroup,
} from '../../types';
import type { DiagramKitId } from '../../utils/diagramKits';

// ── Shared constants used by both initial setup and page actions ────────
export const ROOT_GRAPH_ID = 'root';

export const DEFAULT_LAYER: Layer = {
  id: 'default',
  name: 'Default',
  color: '#6366f1',
  visible: true,
  dimmed: false,
};

/** Persisted-shape (subset) used by the bootstrap helpers below. */
export interface PersistedPageData {
  diagramType?: DiagramType;
  kit?: DiagramKitId;
  nodes?: Node[];
  edges?: Edge[];
  groups?: NodeGroup[];
  layers?: Layer[];
  viewport?: { x: number; y: number; scale: number };
  participants?: SequenceParticipant[];
  messages?: SequenceMessage[];
  fragments?: SequenceFragment[];
  participantGroups?: SequenceParticipantGroup[];
  seqLifelineLength?: number;
  pages?: DiagramPage[];
  activePage?: string;
}

/** State fields the page slice reads or mutates. */
export interface PageMutableState {
  pages: DiagramPage[];
  activePage: string;
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  layers: Layer[];
  viewport: { x: number; y: number; scale: number };
  graphs: Record<string, Graph>;
  activeGraphId: string;
  graphNavStack: Array<{
    graphId: string;
    graphLabel: string;
    returnViewport: { x: number; y: number; scale: number };
  }>;
  groupNavStack: Array<{
    groupId: string;
    groupLabel: string;
    returnViewport: { x: number; y: number; scale: number };
  }>;
  selectedItems: string[];
  diagramType: DiagramType;
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength: number;
}

/** Public action surface contributed by the page slice. */
export interface PageActions {
  addPage: (name?: string, diagramType?: DiagramType, kit?: DiagramKitId) => string;
  switchPage: (pageId: string) => void;
  setPageDiagramType: (pageId: string, diagramType: DiagramType) => void;
  setPageKit: (pageId: string, kit: DiagramKitId) => void;
  renamePage: (pageId: string, name: string) => void;
  setPageDescription: (pageId: string, description: string) => void;
  setPageSettings: (pageId: string, patch: Partial<PageSettings>) => void;
  deletePage: (pageId: string) => void;
  linkNodeToPage: (nodeId: string, pageId: string) => void;
  unlinkNodePage: (nodeId: string) => void;
}

/** Updater signature compatible with `set(produce(...))`. */
export type PageStateUpdater<TState extends PageMutableState> = (
  mutator: (state: TState) => void,
) => void;

// ── Bootstrap / hydration helpers ───────────────────────────────────────

/**
 * Create the legacy "main" page from a flat persisted snapshot.  Used when
 * we encounter a v1 file that pre-dates the multi-page workspace.
 */
export function makeMainPage(state: PersistedPageData): DiagramPage {
  return {
    id: 'main',
    name: 'Main',
    diagramType: state.diagramType ?? 'flow',
    nodes: state.nodes ?? [],
    edges: state.edges ?? [],
    groups: (state.groups ?? []).map((g) => ({ ...g, childGroupIds: g.childGroupIds ?? [] })),
    layers: state.layers?.length ? state.layers : [{ ...DEFAULT_LAYER }],
    viewport: state.viewport ?? { x: 0, y: 0, scale: 1 },
    participants: state.participants ?? [],
    messages: state.messages ?? [],
    fragments: state.fragments ?? [],
    participantGroups: state.participantGroups ?? [],
    seqLifelineLength: state.seqLifelineLength ?? 500,
  };
}

/** Fill in any missing optional fields on a persisted {@link DiagramPage}. */
export function normalizePage(page: DiagramPage): DiagramPage {
  return {
    ...page,
    kit: page.kit,
    diagramType: page.diagramType ?? 'flow',
    groups: (page.groups ?? []).map((g) => ({ ...g, childGroupIds: g.childGroupIds ?? [] })),
    layers: page.layers?.length ? page.layers.map((l) => ({ ...l })) : [{ ...DEFAULT_LAYER }],
    viewport: page.viewport ?? { x: 0, y: 0, scale: 1 },
    participants: page.participants ?? [],
    messages: page.messages ?? [],
    fragments: page.fragments ?? [],
    participantGroups: page.participantGroups ?? [],
    seqLifelineLength: page.seqLifelineLength ?? 500,
    graphs: page.graphs ? { ...page.graphs } : undefined,
    settings: page.settings ? { ...page.settings } : undefined,
  };
}

/**
 * If the persisted snapshot pre-dates per-page sequence data but contains
 * legacy top-level sequence state, attach it to the active page so we don't
 * lose any participants/messages/fragments on upgrade.
 */
export function withLegacySequenceData(
  pages: DiagramPage[],
  persisted: PersistedPageData & { seqLifelineLength?: number },
  activePageId: string,
): DiagramPage[] {
  const hasPageSequenceData = pages.some(
    (page) =>
      (page.participants?.length ?? 0) > 0 ||
      (page.messages?.length ?? 0) > 0 ||
      (page.fragments?.length ?? 0) > 0 ||
      (page.participantGroups?.length ?? 0) > 0,
  );
  if (hasPageSequenceData) return pages;

  const legacyParticipants = persisted.participants ?? [];
  const legacyMessages = persisted.messages ?? [];
  const legacyFragments = persisted.fragments ?? [];
  const legacyParticipantGroups = persisted.participantGroups ?? [];
  const hasLegacySequenceData =
    legacyParticipants.length > 0 ||
    legacyMessages.length > 0 ||
    legacyFragments.length > 0 ||
    legacyParticipantGroups.length > 0 ||
    persisted.diagramType === 'sequence';

  if (!hasLegacySequenceData) return pages;

  return pages.map((page) =>
    page.id === activePageId
      ? {
          ...page,
          diagramType: page.diagramType ?? persisted.diagramType ?? 'sequence',
          participants: legacyParticipants,
          messages: legacyMessages,
          fragments: legacyFragments,
          participantGroups: legacyParticipantGroups,
          seqLifelineLength: persisted.seqLifelineLength ?? page.seqLifelineLength ?? 500,
        }
      : page,
  );
}

/**
 * Snapshot the live store state into the shape of a {@link DiagramPage}.
 * Used both during page switching (to preserve the outgoing page) and
 * during persistence (to keep the active page in sync with `state.*`).
 */
export function syncPageFromState<TState extends PageMutableState>(
  state: TState,
  page: DiagramPage,
): DiagramPage {
  return {
    ...page,
    diagramType: state.diagramType,
    nodes: state.nodes.map((n) => ({ ...n })),
    edges: state.edges.map((e) => ({ ...e })),
    groups: state.groups.map((g) => ({ ...g })),
    layers: state.layers.map((l) => ({ ...l })),
    viewport: { ...state.viewport },
    graphs: { ...state.graphs },
    participants: state.participants.map((p) => ({ ...p })),
    messages: state.messages.map((m) => ({ ...m })),
    fragments: state.fragments.map((f) => ({ ...f })),
    participantGroups: state.participantGroups.map((g) => ({ ...g })),
    seqLifelineLength: state.seqLifelineLength,
  };
}

// ── Internal helpers used by the page actions ───────────────────────────

/** Hydrate `state.*` flat fields from a target page (used by switch/delete). */
function hydrateFlatStateFromPage<TState extends PageMutableState>(
  state: TState,
  page: DiagramPage,
): void {
  const pageGraphs = page.graphs;
  if (pageGraphs && Object.keys(pageGraphs).length > 0) {
    state.graphs = { ...pageGraphs };
  } else {
    state.graphs = {
      [ROOT_GRAPH_ID]: {
        id: ROOT_GRAPH_ID,
        nodes: page.nodes.map((n) => ({ ...n })),
        edges: page.edges.map((e) => ({ ...e })),
        viewport: { ...page.viewport },
      },
    };
  }

  state.activeGraphId = ROOT_GRAPH_ID;
  state.nodes = state.graphs[ROOT_GRAPH_ID]?.nodes ?? [];
  state.edges = state.graphs[ROOT_GRAPH_ID]?.edges ?? [];
  state.groups = page.groups.map((g) => ({ ...g, childGroupIds: g.childGroupIds ?? [] }));
  state.layers = page.layers.length
    ? page.layers.map((l) => ({ ...l }))
    : [{ ...DEFAULT_LAYER }];
  state.viewport = { ...page.viewport };
  state.diagramType = page.diagramType ?? 'flow';
  state.participants = page.participants?.map((p) => ({ ...p })) ?? [];
  state.messages = page.messages?.map((m) => ({ ...m })) ?? [];
  state.fragments = page.fragments?.map((f) => ({ ...f })) ?? [];
  state.participantGroups = page.participantGroups?.map((g) => ({ ...g })) ?? [];
  state.seqLifelineLength = page.seqLifelineLength ?? 500;
  state.selectedItems = [];
  state.groupNavStack = [];
  state.graphNavStack = [];
}

// ── Factory ─────────────────────────────────────────────────────────────

/**
 * Build the page-management action set bound to the host store's updater.
 *
 * `applyUpdate` is expected to wrap Immer's `produce` so the supplied
 * mutator can mutate state directly while still returning a new immutable
 * snapshot upstream.
 */
export function createPageActions<TState extends PageMutableState>(
  applyUpdate: PageStateUpdater<TState>,
): PageActions {
  return {
    addPage: (name, diagramType, kit) => {
      const pageId = generateId('page');
      applyUpdate((state) => {
        state.pages.push({
          id: pageId,
          name: name ?? `Page ${state.pages.length + 1}`,
          diagramType: diagramType ?? 'flow',
          kit: diagramType === 'sequence' ? undefined : (kit ?? 'architecture'),
          nodes: [],
          edges: [],
          groups: [],
          layers: [{ ...DEFAULT_LAYER }],
          viewport: { x: 0, y: 0, scale: 1 },
          participants: [],
          messages: [],
          fragments: [],
          participantGroups: [],
          seqLifelineLength: state.seqLifelineLength ?? 500,
          graphs: {
            [ROOT_GRAPH_ID]: {
              id: ROOT_GRAPH_ID,
              nodes: [],
              edges: [],
              viewport: { x: 0, y: 0, scale: 1 },
            },
          },
        });
      });
      return pageId;
    },

    switchPage: (pageId) =>
      applyUpdate((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page || page.id === state.activePage) return;

        const currentPage = state.pages.find((p) => p.id === state.activePage);
        if (currentPage) {
          Object.assign(currentPage, syncPageFromState(state, currentPage));
        }

        state.activePage = pageId;
        hydrateFlatStateFromPage(state, page);
      }),

    setPageDiagramType: (pageId, diagramType) =>
      applyUpdate((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page || page.diagramType === diagramType) return;
        page.diagramType = diagramType;
        page.participants = page.participants ?? [];
        page.messages = page.messages ?? [];
        page.fragments = page.fragments ?? [];
        page.participantGroups = page.participantGroups ?? [];
        page.seqLifelineLength = page.seqLifelineLength ?? 500;
        if (page.id !== state.activePage) return;
        state.diagramType = diagramType;
        state.selectedItems = [];
      }),

    setPageKit: (pageId, kit) =>
      applyUpdate((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page || page.diagramType === 'sequence') return;
        page.kit = kit;
      }),

    renamePage: (pageId, name) =>
      applyUpdate((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (page) page.name = name;
      }),

    setPageDescription: (pageId, description) =>
      applyUpdate((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (page) page.description = description;
      }),

    /**
     * Merge a patch into the page's overrides. Passing `undefined` for a field
     * clears it, which puts that setting back under the app default rather than
     * pinning the page to whatever the default happens to be today.
     */
    setPageSettings: (pageId, patch) =>
      applyUpdate((state) => {
        const page = state.pages.find((p) => p.id === pageId);
        if (!page) return;
        const merged: PageSettings = { ...page.settings, ...patch };
        for (const key of Object.keys(merged) as (keyof PageSettings)[]) {
          if (merged[key] === undefined) delete merged[key];
        }
        page.settings = Object.keys(merged).length > 0 ? merged : undefined;
      }),

    deletePage: (pageId) =>
      applyUpdate((state) => {
        if (state.pages.length <= 1) return;
        state.pages = state.pages.filter((p) => p.id !== pageId);
        state.nodes.forEach((n) => {
          if (n.linkedPageId === pageId) n.linkedPageId = undefined;
        });
        if (state.activePage === pageId) {
          const nextPage = state.pages[0];
          state.activePage = nextPage.id;
          hydrateFlatStateFromPage(state, nextPage);
        }
      }),

    linkNodeToPage: (nodeId, pageId) =>
      applyUpdate((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) node.linkedPageId = pageId;
        const graphNode = state.graphs[state.activeGraphId]?.nodes.find(
          (n) => n.id === nodeId,
        );
        if (graphNode) graphNode.linkedPageId = pageId;
      }),

    unlinkNodePage: (nodeId) =>
      applyUpdate((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) node.linkedPageId = undefined;
        const graphNode = state.graphs[state.activeGraphId]?.nodes.find(
          (n) => n.id === nodeId,
        );
        if (graphNode) graphNode.linkedPageId = undefined;
      }),
  };
}
